import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { DollarSign, Search, AlertCircle } from 'lucide-react';

export function WalletManager() {
  const { user } = useAuth();
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  const searchUser = async () => {
    if (!userId.trim()) {
      toast.error('Please enter a user ID or email');
      return;
    }

    try {
      setLoading(true);
      
      // Try to find user by ID first
      let query = supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .eq('id', userId)
        .maybeSingle();

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        // Get wallet info
        const { data: walletData } = await supabase
          .from('wallets')
          .select('balance, total_spent, total_earned')
          .eq('user_id', data.id)
          .single();

        setUserInfo({ ...data, wallet: walletData });
        toast.success('User found');
      } else {
        toast.error('User not found');
        setUserInfo(null);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to search user');
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustBalance = async () => {
    if (!userInfo || !amount || !reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    const numAmount = parseInt(amount);
    if (isNaN(numAmount) || numAmount === 0) {
      toast.error('Amount must be a non-zero number');
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('admin_adjust_wallet', {
        _admin_id: user?.id,
        _user_id: userInfo.id,
        _amount: numAmount,
        _reason: reason,
        _notes: notes || null
      });

      if (error) throw error;

      const result = data as { success: boolean; new_balance: number; amount: number; action_type: string };
      toast.success(`Balance adjusted! New balance: ${result.new_balance} coins`);
      
      // Refresh user info
      setAmount('');
      setReason('');
      setNotes('');
      searchUser();
    } catch (error: any) {
      toast.error(error.message || 'Failed to adjust balance');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverPayment = async () => {
    if (!transactionRef || !amount) {
      toast.error('Please enter transaction reference and amount');
      return;
    }

    const numAmount = parseInt(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('recover_failed_payment', {
        _admin_id: user?.id,
        _transaction_reference: transactionRef,
        _recovery_amount: numAmount,
        _notes: notes || null
      });

      if (error) throw error;

      toast.success('Payment recovered successfully!');
      setTransactionRef('');
      setAmount('');
      setNotes('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to recover payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Wallet Management
          </CardTitle>
          <CardDescription>
            Adjust user balances and recover failed payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="adjust" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="adjust">Adjust Balance</TabsTrigger>
              <TabsTrigger value="recover">Recover Payment</TabsTrigger>
            </TabsList>

            <TabsContent value="adjust" className="space-y-6 mt-6">
              {/* User Search */}
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="userId">User ID</Label>
                    <Input
                      id="userId"
                      placeholder="Enter user ID"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchUser()}
                    />
                  </div>
                  <Button 
                    onClick={searchUser} 
                    disabled={loading}
                    className="mt-6"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>

                {userInfo && (
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        {userInfo.avatar_url && (
                          <img 
                            src={userInfo.avatar_url} 
                            alt={userInfo.username}
                            className="h-12 w-12 rounded-full"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{userInfo.display_name || userInfo.username}</p>
                          <p className="text-sm text-muted-foreground">@{userInfo.username}</p>
                          {userInfo.wallet && (
                            <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Balance</p>
                                <p className="font-bold">{userInfo.wallet.balance} coins</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Total Spent</p>
                                <p className="font-bold">{userInfo.wallet.total_spent}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Total Earned</p>
                                <p className="font-bold">{userInfo.wallet.total_earned}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Balance Adjustment Form */}
              {userInfo && (
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label htmlFor="amount">
                      Amount (use negative for debit, positive for credit)
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      placeholder="e.g., 100 or -50"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="reason">Reason *</Label>
                    <Select value={reason} onValueChange={setReason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer_support">Customer Support Request</SelectItem>
                        <SelectItem value="payment_error">Payment Error Correction</SelectItem>
                        <SelectItem value="refund">Refund</SelectItem>
                        <SelectItem value="goodwill">Goodwill Credit</SelectItem>
                        <SelectItem value="penalty">Penalty/Deduction</SelectItem>
                        <SelectItem value="other">Other (specify in notes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Additional details about this adjustment..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="flex items-start gap-2 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-500 mb-1">Important</p>
                      <p className="text-muted-foreground">
                        This action will be permanently logged. Make sure the amount and reason are correct.
                      </p>
                    </div>
                  </div>

                  <Button 
                    onClick={handleAdjustBalance}
                    disabled={loading || !amount || !reason}
                    className="w-full"
                  >
                    {loading ? 'Processing...' : 'Adjust Balance'}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="recover" className="space-y-4 mt-6">
              <div>
                <Label htmlFor="transactionRef">Transaction Reference</Label>
                <Input
                  id="transactionRef"
                  placeholder="Enter failed transaction reference"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="recoverAmount">Recovery Amount (coins)</Label>
                <Input
                  id="recoverAmount"
                  type="number"
                  placeholder="Amount to credit"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="recoverNotes">Notes (optional)</Label>
                <Textarea
                  id="recoverNotes"
                  placeholder="Details about the payment recovery..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex items-start gap-2 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-500 mb-1">Payment Recovery</p>
                  <p className="text-muted-foreground">
                    This will credit the user's wallet and mark the original transaction as refunded.
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleRecoverPayment}
                disabled={loading || !transactionRef || !amount}
                className="w-full"
              >
                {loading ? 'Processing...' : 'Recover Payment'}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
